/* ===========================================================
   Calculadora de margem de contribuição (/calculadora/margem-de-contribuicao).
   Reusa contributionMargin/Pct de domain/roas.ts. Client-side, SSG-safe.
   =========================================================== */
import { useRef, useState } from "react";
import { useSeo } from "../../seo/use-seo";
import { titulo } from "../../seo/site";
import { JsonLd, breadcrumbLd, webAppLd } from "../../seo/jsonld";
import { contributionMargin, contributionMarginPct } from "../../domain/roas";
import { fmt } from "../../domain/format";
import { useContentTracking } from "../../analytics/content-tracking";
import { calculadoraUsada } from "../../analytics/events";
import { ConteudoShell, CtaDemo } from "./shell";

const HOME = { nome: "Início", path: "/" };
const BLOG = { nome: "Blog", path: "/blog" };
const CALC = {
  nome: "Calculadora de margem de contribuição",
  path: "/calculadora/margem-de-contribuicao",
};

function num(s: string): number {
  const n = Number(s.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function Campo({
  label,
  valor,
  onChange,
  afixo,
  lado,
}: {
  label: string;
  valor: string;
  onChange: (v: string) => void;
  afixo: string;
  lado: "esq" | "dir";
}) {
  return (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
        {lado === "esq" && (
          <span style={{ position: "absolute", left: 12, color: "var(--ink-soft)", fontSize: 14 }}>
            {afixo}
          </span>
        )}
        <input
          className="input"
          inputMode="decimal"
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          style={{
            padding: lado === "esq" ? "10px 12px 10px 40px" : "10px 38px 10px 12px",
            borderRadius: 10,
            fontSize: 16,
          }}
        />
        {lado === "dir" && (
          <span style={{ position: "absolute", right: 12, color: "var(--ink-soft)", fontSize: 14 }}>
            {afixo}
          </span>
        )}
      </span>
    </label>
  );
}

export function CalculadoraMargem() {
  useSeo({
    title: titulo("Calculadora de margem de contribuição grátis"),
    description:
      "Calcule a margem de contribuição de um produto em reais e em percentual. Informe preço, CMV, frete e imposto e descubra quanto cada venda deixa antes da mídia.",
    path: "/calculadora/margem-de-contribuicao",
  });
  useContentTracking({ tipo: "calculadora", titulo: "Margem de contribuição" });

  // "Calculadora Usada" 1x quando o visitante mexe em qualquer campo.
  const usou = useRef(false);
  const marcarUso = () => {
    if (!usou.current) {
      usou.current = true;
      calculadoraUsada({ calc: "margem-de-contribuicao" });
    }
  };

  const [preco, setPreco] = useState("149,90");
  const [cmv, setCmv] = useState("60");
  const [frete, setFrete] = useState("18");
  const [imposto, setImposto] = useState("6");

  const p = num(preco);
  const custosVariaveis = num(cmv) + num(frete) + p * (num(imposto) / 100);
  const margem = contributionMargin(p, custosVariaveis); // R$
  const margemPct = contributionMarginPct(p, custosVariaveis); // fração | null

  return (
    <ConteudoShell breadcrumb={[HOME, BLOG, CALC]}>
      <JsonLd data={breadcrumbLd([HOME, BLOG, CALC])} />
      <JsonLd
        data={webAppLd({
          nome: "Calculadora de margem de contribuição",
          descricao:
            "Calcule a margem de contribuição (R$ e %) de um produto a partir de preço, CMV, frete e imposto.",
          path: "/calculadora/margem-de-contribuicao",
        })}
      />
      <h1 id="conteudo-h1">Calculadora de margem de contribuição</h1>
      <p className="legal-data">
        Quanto cada venda deixa depois dos custos variáveis (CMV, frete e imposto) — antes da mídia
        e dos custos fixos.
      </p>

      <div className="card" style={{ padding: 22, display: "grid", gap: 16, marginTop: 8 }}>
        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr 1fr" }}>
          <Campo
            label="Preço de venda"
            valor={preco}
            onChange={(v) => {
              marcarUso();
              setPreco(v);
            }}
            afixo="R$"
            lado="esq"
          />
          <Campo
            label="CMV (custo do produto)"
            valor={cmv}
            onChange={(v) => {
              marcarUso();
              setCmv(v);
            }}
            afixo="R$"
            lado="esq"
          />
          <Campo
            label="Frete por sua conta"
            valor={frete}
            onChange={(v) => {
              marcarUso();
              setFrete(v);
            }}
            afixo="R$"
            lado="esq"
          />
          <Campo
            label="Imposto sobre a venda"
            valor={imposto}
            onChange={(v) => {
              marcarUso();
              setImposto(v);
            }}
            afixo="%"
            lado="dir"
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: 14,
            gridTemplateColumns: "1fr 1fr",
            borderTop: "1px solid var(--linha)",
            paddingTop: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>Margem de contribuição</div>
            <strong style={{ fontSize: 28, color: margem >= 0 ? "var(--mint)" : "#ff8a8a" }}>
              {fmt.brl(margem, { cents: true })}
            </strong>
          </div>
          <div>
            <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>% da receita</div>
            <strong style={{ fontSize: 28, color: "var(--ink)" }}>
              {margemPct == null ? "—" : fmt.pct(margemPct, { dec: 1 })}
            </strong>
          </div>
        </div>

        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--ink-soft)" }}>
          {margem < 0
            ? "Margem negativa: este produto perde dinheiro antes mesmo de gastar em anúncio. Revise preço ou custos."
            : margemPct != null
              ? `Cada venda deixa ${fmt.brl(margem, { cents: true })} para pagar mídia, custos fixos e virar lucro. Isso dá um ROAS de equilíbrio de ${fmt.roas(1 / margemPct)}.`
              : "Informe um preço de venda para calcular."}
        </p>
      </div>

      <h2>A fórmula</h2>
      <p>
        <strong>Margem de contribuição = preço − CMV − frete − imposto</strong>. É a base do ROAS de
        equilíbrio: quanto menor a margem, mais alto o ROAS precisa ser para a campanha não dar
        prejuízo.
      </p>

      <CtaDemo
        titulo="Veja a margem real de cada produto da sua loja"
        sub="O Lucraa+ calcula a margem de contribuição por SKU com os custos reais da sua Nuvemshop e mostra quais produtos sustentam — ou afundam — o lucro da operação."
      />
    </ConteudoShell>
  );
}
